import {Dialog, IconButton, TextField} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {debounce} from 'lodash';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {trackPanelBankEvent} from '../../../services/analytics';
import {usePanelSearchInputContext} from '../../../state/panelbank/PanelSearchContextProvider';

/**
 * Long enough that typing out a regex reports one event rather than one per
 * keystroke.
 */
const TRACK_DEBOUNCE_MS = 1000;

const PanelSearchHideControl = () => {
  const {hideSearchQuery, onChangeHideSearchQuery} =
    usePanelSearchInputContext();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hasHideRegex = hideSearchQuery.trim().length > 0;

  // Only report queries that differ from the last reported one, so editing a
  // regex back to its previous value doesn't report a change
  const trackedQueryRef = useRef(hideSearchQuery.trim());
  const trackQueryChange = useMemo(
    () =>
      // oxlint-disable-next-line react/refs
      debounce((newHideSearchQuery: string) => {
        const trimmed = newHideSearchQuery.trim();
        if (trimmed === trackedQueryRef.current) {
          return;
        }
        trackedQueryRef.current = trimmed;
        trackPanelBankEvent({
          action:
            trimmed === ''
              ? 'Clear hide panels regex'
              : 'Apply hide panels regex',
        });
      }, TRACK_DEBOUNCE_MS),
    []
  );
  useEffect(
    () => () => {
      trackQueryChange.cancel();
    },
    [trackQueryChange]
  );

  const onChangeQuery = useCallback(
    (newHideSearchQuery: string) => {
      onChangeHideSearchQuery(newHideSearchQuery);
      trackQueryChange(newHideSearchQuery);
    },
    [onChangeHideSearchQuery, trackQueryChange]
  );

  const onToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, [setIsOpen]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <>
      <IconButton
        ref={triggerRef}
        aria-label="Hide panels"
        data-test="panel-search-hide-control"
        icon="panel-hide-icon-closed-eye"
        isActive={hasHideRegex || isOpen}
        onPress={onToggle}
        tooltip={{children: 'Hide panels', placement: 'bottom'}}
        variant="secondary"
      />
      <Dialog.Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        triggerRef={triggerRef}
        placement="bottom end"
        p={6}
        aria-label="Hide panels">
        <Tailwind>
          {/* This is a popover so it is a valid usecase for calc */}
          {/* oxlint-disable-next-line wandb/no-calc-100vh-pixel-math */}
          <div className="w-[min(460px,calc(100vw-32px))]">
            <div className="mb-12 flex items-center justify-between gap-12">
              <h3 className="m-0 text-lg font-semibold text-moon-800 dark:text-moon-50">
                Hide panels
              </h3>
              <IconButton
                aria-label="Close hide panels"
                icon="close"
                onPress={onClose}
                size="sm"
                variant="ghost"
              />
            </div>
            <TextField
              aria-label="Hide from results regex"
              fullWidth
              placeholder="e.g. eval|debug"
              value={hideSearchQuery}
              onChange={onChangeQuery}
            />
            <p className="mb-0 mt-12 text-sm text-wb-text-tertiary">
              Hide panels matching this regex pattern.
            </p>
          </div>
        </Tailwind>
      </Dialog.Popover>
    </>
  );
};

export default memo(PanelSearchHideControl);
